import { Button as DxButton } from 'devextreme-react/button';
import { Column, Summary, ToolbarItem } from 'devextreme-react/data-grid';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useAuth } from '../../../auth';
import Breadcrumb from '../../../components/common/breadcrumb';
import DatagridCustom from '../../../components/common/datagrid/custom-datagrid';
import api from '../../../services/api-configuration';
import { ButtonOnExporting } from '../../../utils/DataGridExporter';
import useFormatMessage from '../../../utils/useFormatMessage';
import AdresIslemleri from './AdresIslemleri';

const INITIAL_ADDRESS = {
    id: null,
    warehouse_id: null,
    warehouse_name: "",
    zone_id: null,
    zone_name: "",
    coordinate: null,
    direction: null,
    row: null,
    capacity: null,
    is_active: true,
    create_date: null
};

// Memoized sub-components
const MemoizedBreadcrumb = React.memo(Breadcrumb);
const MemoizedAdresIslemleri = React.memo(AdresIslemleri);

const StatusCell = React.memo(({ value }) => (
    <span style={{
        // padding: '4px 8px',
        // borderRadius: '4px',
        // backgroundColor: value === 'BOŞ' ? '#e8f5e9' : '#fff3e0',
        // color: value === 'BOŞ' ? '#2e7d32' : '#ef6c00'
    }}>
        {value}
    </span>
));

const ActionButton = React.memo(({ data, onEdit }) => {
    const { hasRole } = useAuth(6);

    if (!hasRole('adres_role_update')) return null;

    return (
        <i
            className="fa fa-edit ml-2"
            onClick={() => onEdit(data)}
            style={{
                cursor: 'pointer',
                color: 'blue',
                fontSize: '18px'
            }}
        />
    );
});

const AdresTanim = () => {
    const { t } = useFormatMessage();
    const { hasRole } = useAuth(6);

    const isMounted = useRef(true);
    const gridRef = useRef(null);

    const [isOpen, setIsOpen] = React.useState(false);
    const [deleteModal, setDeleteModal] = React.useState(false);
    const [dataSource, setDataSource] = React.useState([]);
    const [trigger, setTrigger] = React.useState(false);
    const [selected, setSelected] = React.useState(INITIAL_ADDRESS);
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAddresses = useCallback(async () => {
        if (!isMounted.current || isLoading) return;

        setIsLoading(true);
        if (gridRef.current) {
            gridRef.current.instance.beginCustomLoading(t('Yükleniyor'));
        }

        try {
            const response = await api.post('/adress/getAll');
            if (isMounted.current) {
                let allData = response.data.data || [];
                allData = allData.filter(item =>
                    !['GEMİ', 'SANAL', 'FABRİKA'].includes(item.warehouse_name)
                );
                setDataSource(_.orderBy(allData, ['id'], ['asc']));
            }
        } catch (err) {
            console.error('Address fetch error:', err);
            if (isMounted.current) {
                toast.error(t('Adres Listesi Alınırken hata oluştu'));
                setDataSource([]);
            }
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
                if (gridRef.current) {
                    gridRef.current.instance.endCustomLoading();
                }
            }
        }
    }, [isLoading]);

    React.useEffect(() => {
        fetchAddresses();
    }, [trigger]);

    const handleDelete = useCallback(async () => {
        try {
            const employeeId = parseInt(Buffer.from(localStorage.getItem('employee_id'), 'base64').toString('ascii'));
            await api.post('/address/delete', {
                id: selected.id,
                update_user_id: employeeId
            });

            if (!isMounted.current) return;

            toast.success(t('Adres Silme Başarılı'));
            setDeleteModal(false);
            setTrigger(prev => !prev);
        } catch (err) {
            if (!isMounted.current) return;

            const errorMessage = err.response?.data?.error?.split(':');
            if (errorMessage) {
                const message = t('Adres Silme Başarısız') + ': ' + t(errorMessage[0]);
                toast.error(errorMessage[1] ? `${message} ID: ${t(errorMessage[1])}` : message);
                return;
            }
            toast.error(t('Adres Silme Başarısız'));
        }
    }, [selected.id]);

    const handleAddNew = useCallback(() => {
        setSelected(INITIAL_ADDRESS);
        setIsOpen(true);
    }, []);

    const handleRefresh = useCallback(() => {
        if (!isLoading) {
            setTrigger(prev => !prev);
        }
    }, [isLoading]);

    const handleExport = useCallback((e) => {
        ButtonOnExporting(
            e,
            gridRef,
            `Adres Listesi - ${moment(new Date()).format('DD/MM/YYYY')}`,
            'Sayafa-1'
        );
    }, []);

    const handleEdit = useCallback((data) => {
        setSelected(data);
        setIsOpen(true);
    }, []);

    const toggleDeleteModal = useCallback(() => setDeleteModal(prev => !prev), []);

    const columns = useMemo(() => [
        {
            caption: t('Adres'),
            dataField: 'address_code',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 120,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Depo Adı'),
            dataField: 'warehouse_name',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 150,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Bölge Adı'),
            dataField: 'zone_name',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 150,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Koordinat'),
            dataField: 'coordinate',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 120,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Yön'),
            dataField: 'direction',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 100,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Total Kapasite'),
            dataField: 'capacity',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 120,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Mevcut Araç'),
            dataField: 'used_capacity',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 100,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Durum'),
            dataField: 'status',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 120,
            fixed: true,
            allowResizing: false,
            cellRender: ({ value }) => <StatusCell value={value} />
        },
        {
            caption: t('İşlem'),
            alignment: 'center',
            width: 70,
            fixed: true,
            allowResizing: false,
            allowExporting: false,
            cellRender: ({ data }) => <ActionButton data={data} onEdit={handleEdit} />
        }
    ], [t, handleEdit]);

    const ActionButtons = () => {
        return (
            <div className="card-toolbar d-flex justify-content-end align-items-center mb-0"
                style={{ gap: '5px' }}
            >
                <DxButton
                    stylingMode="outlined"
                    type="default"
                    onClick={handleRefresh}
                    disabled={isLoading}
                    text=""
                    width={38}
                    height={38}
                >
                    <i className="fa fa-refresh" />
                </DxButton>

                {hasRole('adres_role_write') && (
                    <DxButton
                        type="default"
                        stylingMode="contained"
                        onClick={handleAddNew}
                        disabled={isLoading}
                        width={90}
                        height={38}
                    >
                        <div className="d-flex align-items-center justify-content-center">
                            <i className="fa fa-plus" />&nbsp;
                            <span className="ms-2">{t('Ekle')}</span>
                        </div>
                    </DxButton>
                )}

                {hasRole('adres_role_export') && (
                    <DxButton
                        stylingMode="contained"
                        type="success"
                        onClick={handleExport}
                        disabled={isLoading}
                        width={38}
                        height={38}
                        title={t('Excele Aktar')}
                    >
                        <i className="fa fa-file-excel-o" />
                    </DxButton>
                )}
            </div>
        );
    };

    return (
        <>
            <MemoizedBreadcrumb current={t('Adres Tanım')} />
            <Card className="p-4">
                <ActionButtons />
                <Row>
                    <DatagridCustom
                        dataSource={dataSource}
                        model="adres-tanim"
                        reff={gridRef}
                        height="75vh"
                    >
                        {/* <Scrolling
                            mode="infinite"
                            // rowRenderingMode="virtual"
                            // useNative={false}
                            scrollByContent={true}
                            // scrollByThumb={true}
                            showScrollbar="always"
                            // preloadEnabled={true}
                            // timeout={200}
                            // updateTimeout={150}
                            // columnRenderingMode="virtual"
                            reloadMode="paged"
                        />
                        <Paging enabled={false} /> */}

                        {columns.map(column => (
                            <Column key={column.dataField || column.caption} {...column} />
                        ))}

                        <Summary>
                            <ToolbarItem
                                name="toplam"
                                location="after"
                                text={t('Toplam')}
                            />
                        </Summary>
                    </DatagridCustom>
                </Row>
            </Card>

            <MemoizedAdresIslemleri
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setTrigger={setTrigger}
                trigger={trigger}
                selected={selected}
            />

            <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
                <ModalHeader toggle={toggleDeleteModal}>
                    {t('Adres Silme Formu')}
                </ModalHeader>
                <ModalBody>
                    {t('Depo')}: <b>{selected?.warehouse_name}</b> <br />
                    {t('Bölge')}: <b>{selected?.zone_name}</b> <br />
                    {t('Adres Silme İşlemi Yapmak İstediğinize Emin Misiniz?')}
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={handleDelete}>
                        {t('Sil')}
                    </Button>
                    <Button color="secondary" onClick={toggleDeleteModal}>
                        {t('İptal')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default React.memo(AdresTanim);